import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useParams, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, FileText, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import PostCard from '../components/blog/PostCard';
import AuthorSocialLinks from '../components/blog/AuthorSocialLinks';
import ShareButtons from '../components/blog/ShareButtons';
import { Container } from '../components/ui/Container';
import Pagination from '../components/ui/Pagination';
import Spinner from '../components/ui/Spinner';
import SEO from '../components/seo/SEO';
import StructuredData, { generateAuthorSchema, generateBreadcrumbSchema } from '../components/seo/StructuredData';
import CrawlerSeoContent from '../components/seo/CrawlerSeoContent';
import { fetchAuthorBySlug, fetchAuthorArticles } from '../store/actions/authorActions';
import { transformAuthor, transformArticles } from '../utils/transformData';
import { isMongoObjectId } from '../utils/slugUtils';
import { useRegionalSeo } from '../hooks/useRegionalSeo';
import { useSeoExtras } from '../context/SiteSeoContext';

const PAGE_SIZE = 9;

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;

    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }

    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.mutedForeground};

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: ${(props) => props.theme.colors.primary};
      text-decoration: underline;
    }
  }

  span[aria-hidden='true'] {
    opacity: 0.5;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
  font-size: 0.9375rem;
  color: ${(props) => props.theme.colors.mutedForeground};
  text-decoration: none;

  &:hover {
    color: ${(props) => props.theme.colors.foreground};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const AuthorHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 2.5rem;
  padding: 2rem 1.5rem;
  background-color: ${(props) => props.theme.colors.card};
  border-radius: ${(props) => props.theme.borderRadius['2xl']};
  border: 1px solid ${(props) => props.theme.colors.border};

  @media (min-width: 768px) {
    padding: 2.75rem 3rem;
    margin-bottom: 3rem;
  }
`;

const AuthorAvatar = styled.div`
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 9999px;
  overflow: hidden;
  background-color: ${(props) => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  box-shadow: 0 0 0 4px ${(props) => props.theme.colors.background},
    0 0 0 5px ${(props) => props.theme.colors.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: 1024px) {
    width: 7rem;
    height: 7rem;
  }
`;

const AvatarLetter = styled.span`
  color: ${(props) => props.theme.colors.primaryForeground};
  font-family: ${(props) => props.theme.typography.fonts.display};
  font-weight: ${(props) => props.theme.typography.fontWeights.bold};
  font-size: 2rem;
`;

const AuthorName = styled.h1`
  font-family: ${(props) => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;

  @media (min-width: 1024px) {
    font-size: 2.75rem;
  }
`;

const AuthorBio = styled.p`
  font-size: 1rem;
  color: ${(props) => props.theme.colors.mutedForeground};
  max-width: 42rem;
  line-height: 1.7;
  margin-bottom: 0.25rem;
  white-space: pre-wrap;
`;

const BioToggle = styled.button`
  border: none;
  background: none;
  color: ${(props) => props.theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0.5rem;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const JoinedRow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: ${(props) => props.theme.colors.mutedForeground};

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const AuthorStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${(props) => props.theme.colors.border};
  width: 100%;
  max-width: 28rem;
`;

const StatItem = styled.div`
  text-align: center;
  min-width: 5.5rem;
`;

const StatValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 1.375rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};

  svg {
    width: 1rem;
    height: 1rem;
    color: ${(props) => props.theme.colors.mutedForeground};
  }
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.colors.mutedForeground};
`;

const ArticlesSection = styled.section`
  scroll-margin-top: 5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${(props) => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${(props) => props.theme.typography.fontWeights.semibold};
  margin: 0 0 0.35rem;
`;

const ResultsMeta = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.mutedForeground};
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const SortSelect = styled.select`
  appearance: none;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.border};
  background: ${(props) => props.theme.colors.card}
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")
    no-repeat right 0.65rem center;
  color: ${(props) => props.theme.colors.foreground};
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const FilterChip = styled.button`
  border: 1px solid
    ${(props) => (props.$active ? props.theme.colors.primary : props.theme.colors.border)};
  background: ${(props) =>
    props.$active ? `${props.theme.colors.primary}18` : props.theme.colors.card};
  color: ${(props) =>
    props.$active ? props.theme.colors.primary : props.theme.colors.foreground};
  border-radius: 9999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  opacity: ${(props) => (props.$dimmed ? 0.55 : 1)};
  transition: opacity 0.2s;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
  color: ${(props) => props.theme.colors.mutedForeground};

  p {
    margin-bottom: 1rem;
  }
`;

const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(num);
};

const Author = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { slug: slugParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, apiRegion, getAuthorUrl } = useLanguage();
  const decodedSlug = decodeURIComponent(slugParam || '');
  const regionalSeo = useRegionalSeo(`/author/${slugParam || ''}`);
  const authorsRegionalSeo = useRegionalSeo('/authors');
  const seoExtras = useSeoExtras();
  const activeLanguage = language;
  const articlesRef = useRef(null);
  const [bioExpanded, setBioExpanded] = useState(false);

  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const sort = searchParams.get('sort') === 'popular' ? 'popular' : 'newest';
  const categoryFilter = searchParams.get('category') || '';

  const {
    author: authorFromStore,
    authorArticles: authorArticlesFromStore,
    authorFilters,
    pagination,
    loadingProfile,
    loadingArticles,
    error,
  } = useSelector((state) => state.author);

  useEffect(() => {
    if (decodedSlug) {
      dispatch(fetchAuthorBySlug(decodedSlug, activeLanguage, apiRegion));
    }
  }, [dispatch, decodedSlug, activeLanguage, apiRegion]);

  useEffect(() => {
    if (decodedSlug) {
      dispatch(
        fetchAuthorArticles(decodedSlug, {
          page: currentPage,
          limit: PAGE_SIZE,
          lang: activeLanguage,
          region: apiRegion,
          sort,
          category: categoryFilter || undefined,
        })
      );
    }
  }, [dispatch, decodedSlug, currentPage, activeLanguage, apiRegion, sort, categoryFilter]);

  useEffect(() => {
    setBioExpanded(false);
  }, [decodedSlug]);

  const author = authorFromStore ? transformAuthor(authorFromStore) : null;
  const authorArticles = transformArticles(
    authorArticlesFromStore?.data || authorArticlesFromStore || []
  );
  const totalArticles = pagination?.total ?? 0;
  const filterCategories = authorFilters?.categories || [];

  const rangeStart = totalArticles === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalArticles);

  const updateParams = useCallback(
    (patch, { resetPage = false } = {}) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          value === '' ||
          (key === 'sort' && value === 'newest') ||
          (key === 'page' && Number(value) <= 1)
        ) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // If filters shrink results below the current page, jump back to page 1
  useEffect(() => {
    if (!loadingArticles && pagination?.pages > 0 && currentPage > pagination.pages) {
      updateParams({ page: null });
    }
  }, [loadingArticles, pagination?.pages, currentPage, updateParams]);

  const handlePageChange = (page) => {
    updateParams({ page });
    requestAnimationFrame(() => {
      articlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleSortChange = (e) => {
    updateParams({ sort: e.target.value }, { resetPage: true });
  };

  const handleCategoryFilter = (slug) => {
    const next = slug && slug !== categoryFilter ? slug : null;
    updateParams({ category: next }, { resetPage: true });
  };

  const bioText = author?.bio || '';
  const bioLong = bioText.length > 280;
  const shownBio = bioLong && !bioExpanded ? `${bioText.slice(0, 280).trim()}…` : bioText;

  const joinedLabel = useMemo(() => {
    if (!author?.createdAt) return null;
    try {
      return new Date(author.createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
      });
    } catch {
      return null;
    }
  }, [author?.createdAt]);

  if (loadingProfile && !author) {
    return (
      <PageWrapper>
        <Header />
        <Main>
          <Container>
            <Spinner />
          </Container>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }

  if (!author && !loadingProfile && error) {
    return (
      <PageWrapper>
        <SEO noindex title="Author Not Found" description="The author you're looking for doesn't exist." />
        <Header />
        <Main>
          <Container>
            <EmptyState>
              <p>{t('author.notFound', { defaultValue: 'Author not found.' })}</p>
              <BackLink to={authorsRegionalSeo.pageUrl}>
                <ArrowLeft />
                {t('author.allAuthors')}
              </BackLink>
            </EmptyState>
          </Container>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }

  if (author?.slug && slugParam && isMongoObjectId(decodedSlug) && author.slug !== decodedSlug) {
    return <Navigate to={getAuthorUrl(author.slug)} replace />;
  }

  const authorUrl = getAuthorUrl(author?.slug || decodedSlug);
  const homeUrl = regionalSeo.alternateUrls[activeLanguage] || '/';
  const authorsUrl = authorsRegionalSeo.pageUrl;
  const displayName = author?.name || decodedSlug;

  return (
    <PageWrapper>
      <SEO
        strict={Boolean(author?.seo?.metaTitle)}
        title={author?.seo?.metaTitle || author?.name}
        description={author?.seo?.metaDescription || author?.bio}
        url={authorUrl}
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
        {...seoExtras}
      />
      {author && <StructuredData data={generateAuthorSchema(author)} />}
      {author && (
        <StructuredData
          data={generateBreadcrumbSchema([
            { name: 'Home', url: homeUrl },
            { name: 'Authors', url: authorsUrl },
            { name: author.name, url: authorUrl },
          ])}
        />
      )}
      {author && <CrawlerSeoContent title={author.name} intro={author.bio} />}
      <Header />
      <Main>
        <Container>
          <Breadcrumb aria-label="Breadcrumb">
            <Link to={homeUrl}>{t('nav.home', { defaultValue: 'Home' })}</Link>
            <span aria-hidden="true">/</span>
            <Link to={authorsUrl}>{t('author.allAuthors')}</Link>
            <span aria-hidden="true">/</span>
            <span>{displayName}</span>
          </Breadcrumb>

          <BackLink to={authorsUrl}>
            <ArrowLeft />
            {t('author.allAuthors')}
          </BackLink>

          <AuthorHeader>
            <AuthorAvatar>
              {author?.avatar ? (
                <img src={author.avatar} alt={displayName} />
              ) : (
                <AvatarLetter>{displayName.charAt(0)}</AvatarLetter>
              )}
            </AuthorAvatar>
            <AuthorName>{displayName}</AuthorName>
            {bioText ? (
              <>
                <AuthorBio>{shownBio}</AuthorBio>
                {bioLong && (
                  <BioToggle type="button" onClick={() => setBioExpanded((v) => !v)}>
                    {bioExpanded
                      ? t('common.showLess', { defaultValue: 'Show less' })
                      : t('author.readMore', { defaultValue: 'Read more' })}
                  </BioToggle>
                )}
              </>
            ) : null}

            <AuthorSocialLinks
              socialLinks={author?.socialLinks}
              customLinks={author?.customLinks}
            />

            {joinedLabel && (
              <JoinedRow>
                <Calendar />
                {t('author.joined', {
                  date: joinedLabel,
                  defaultValue: `Writing since ${joinedLabel}`,
                })}
              </JoinedRow>
            )}

            <HeaderActions>
              <ShareButtons
                title={displayName}
                url={typeof window !== 'undefined' ? window.location.href : authorUrl}
                description={author?.bio || ''}
              />
            </HeaderActions>

            <AuthorStats>
              <StatItem>
                <StatValue>
                  <FileText />
                  {formatCount(totalArticles || author?.articleCount || 0)}
                </StatValue>
                <StatLabel>
                  {t('author.articlesCount', {
                    count: totalArticles || author?.articleCount || 0,
                  })}
                </StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>
                  <Eye />
                  {formatCount(author?.totalViews || 0)}
                </StatValue>
                <StatLabel>{t('author.totalViews', { defaultValue: 'Total views' })}</StatLabel>
              </StatItem>
            </AuthorStats>
          </AuthorHeader>

          <ArticlesSection ref={articlesRef} id="author-articles">
            <SectionHeader>
              <div>
                <SectionTitle>
                  {t('author.articlesByAuthor', { author: displayName })}
                </SectionTitle>
                <ResultsMeta>
                  {totalArticles > 0
                    ? t('author.showingRange', {
                        start: rangeStart,
                        end: rangeEnd,
                        total: totalArticles,
                        defaultValue: `Showing ${rangeStart}–${rangeEnd} of ${totalArticles}`,
                      })
                    : t('author.noArticles')}
                </ResultsMeta>
              </div>
              <Controls>
                <SortSelect
                  id="author-sort"
                  value={sort}
                  onChange={handleSortChange}
                  aria-label={t('author.sortLabel', { defaultValue: 'Sort articles' })}
                >
                  <option value="newest">
                    {t('author.sortNewest', { defaultValue: 'Newest' })}
                  </option>
                  <option value="popular">
                    {t('author.sortPopular', { defaultValue: 'Most viewed' })}
                  </option>
                </SortSelect>
              </Controls>
            </SectionHeader>

            {filterCategories.length > 1 && (
              <FilterRow
                aria-label={t('author.filterCategory', { defaultValue: 'Filter by category' })}
              >
                <FilterChip
                  type="button"
                  $active={!categoryFilter}
                  onClick={() => handleCategoryFilter('')}
                >
                  {t('author.allCategories', { defaultValue: 'All topics' })}
                </FilterChip>
                {filterCategories.map((cat) => (
                  <FilterChip
                    key={cat.slug}
                    type="button"
                    $active={categoryFilter === cat.slug}
                    onClick={() => handleCategoryFilter(cat.slug)}
                  >
                    {cat.name}
                  </FilterChip>
                ))}
              </FilterRow>
            )}

            {loadingArticles && authorArticles.length === 0 ? (
              <Spinner />
            ) : authorArticles.length > 0 ? (
              <>
                <PostsGrid $dimmed={loadingArticles}>
                  {authorArticles.map((post, index) => (
                    <PostCard key={post._id || `${post.title}-${index}`} {...post} index={index} />
                  ))}
                </PostsGrid>
                {pagination?.pages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.pages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <EmptyState>
                <p>
                  {categoryFilter
                    ? t('author.noArticlesInCategory', {
                        defaultValue: 'No articles in this category for this author.',
                      })
                    : t('author.noArticles')}
                </p>
                {categoryFilter && (
                  <FilterChip type="button" onClick={() => handleCategoryFilter('')}>
                    {t('author.clearFilter', { defaultValue: 'Clear filter' })}
                  </FilterChip>
                )}
              </EmptyState>
            )}
          </ArticlesSection>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Author;
